import React from "react";
import { I18n } from "@aws-amplify/core";
import { withStyles } from "@material-ui/styles";
import { get, map, max, sum, sortBy, flatten } from "lodash";
import Grid from "@material-ui/core/Grid";

import HspChartValue from "./HspChartValue";
import DashboardCard from "../DashboardCard";
import {
  sizes,
  activeCases,
  failedSurveys,
  feverdetected,
} from "../../../constants/common";
import colors from "../../../assets/style/colors";

const styles = {
  legendColor: {
    minWidth: "16px",
    width: "16px",
    height: "16px",
    borderRadius: "3px",
  },
  legendTitle: {
    color: colors.black87,
    marginRight: "35px",
  },
  legendValue: {
    color: colors.black87,
    fontWeight: 500,
    margin: "0px 10px",
  },
  chartContainer: {
    height: "100%",
    maxHeight: "371px",
    marginTop: "15px",
    overflowY: "scroll",
  },
  chartGrid: {
    position: "absolute",
    height: "389px",
    bottom: 0,
    left: "20px",
    maxWidth: "calc(100% - 36px)",
  },
  chartGridItem: {
    borderTop: "none",
    borderRight: "none",
    borderLeft: `1px solid ${colors.greyBadgeBg}`,
    borderBottom: `1px solid ${colors.greyBadgeBg}`,
    height: "100%",
  },
  scaleNb: {
    height: "16px",
    fontSize: "0.857em",
    color: colors.black54,
    backgroundColor: colors.white,
  },
};

const HspChart = (props) => {
  const { classes } = props;
  const [containerWidth, setContainerWidth] = React.useState(275);

  React.useEffect(() => {
    window.addEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    handleResize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.getElementById("hspChart")]);

  const handleResize = () => {
    const width = get(document.getElementById("hspChart"), ["offsetWidth"]);
    setContainerWidth(width - 52);
  };

  const barColors = [colors.redCoral, colors.yellowDoughnut, colors.bodyPink];

  const data = sortBy(
    get(props, ["data"]),
    (item) => -(item.activeCases + item.failedSurveys + item.feverdetected)
  );

  const maxValue = max(
    flatten(
      map(data, (item) => [
        item.activeCases,
        item.failedSurveys,
        item.feverdetected,
      ])
    )
  );

  const gridNb = 20;
  const gridSpace = (containerWidth - 39) / gridNb;

  let maxScaleValue = maxValue || 20;
  while (maxScaleValue % 20 !== 0) {
    maxScaleValue++;
  }

  return (
    <DashboardCard
      title={I18n.get("dashboard_hspSitesRiskChart")}
      size={sizes.medium}
      id="hspChart"
    >
      <Grid
        container
        alignItems="center"
        justify="center"
        style={{ height: "13px", marginTop: "-5px" }}
      >
        {map([activeCases, failedSurveys, feverdetected], (type, index) => (
          <React.Fragment key={index}>
            <Grid
              item
              className={classes.legendColor}
              style={{ backgroundColor: barColors[index] }}
            />
            <Grid item className={classes.legendValue}>
              {sum(map(data, (d) => get(d, [type])))}
            </Grid>
            <Grid
              item
              className={classes.legendTitle}
              style={{ marginRight: index === 2 ? 0 : "" }}
            >
              {I18n.get(`dashboard_${type}`)}
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
      <Grid
        container
        alignItems="center"
        className={classes.chartContainer}
      >
        <Grid
          item
          style={{ width: containerWidth, height: "371px", zIndex: 100 }}
        >
          {map(data, (item, index) => (
            <HspChartValue
              key={index}
              item={item}
              barColors={barColors}
              maxValue={maxScaleValue}
            />
          ))}
        </Grid>
      </Grid>
      <Grid item className={classes.chartGrid}>
        <Grid
          container
          direction="column"
          wrap="nowrap"
          style={{ height: "100%" }}
        >
          <Grid item style={{ height: "100%" }}>
            <Grid container style={{ height: "100%" }}>
              {map(new Array(gridNb), (i, index) => (
                <Grid
                  item
                  className={classes.chartGridItem}
                  key={index}
                  style={{
                    width: `${gridSpace}px`,
                    borderRight:
                      index === gridNb - 1
                        ? `1px solid ${colors.greyBadgeBg}`
                        : "none",
                  }}
                />
              ))}
            </Grid>
          </Grid>
          <Grid item>
            <Grid
              container
              wrap="nowrap"
              style={{ marginLeft: "-4px" }}
              alignItems="center"
            >
              {map(new Array(gridNb + 1), (i, index) => (
                <Grid
                  item
                  key={index}
                  className={classes.scaleNb}
                  style={{
                    width: index === gridNb ? "" : `${gridSpace + 1}px`,
                    minWidth: index === gridNb ? "" : `${gridSpace + 1}px`,
                  }}
                >
                  {(index * maxScaleValue) / gridNb}
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </DashboardCard>
  );
};

export default withStyles(styles)(HspChart);
